import { useState } from 'react';

import {
  unlockAffiliateDashboard, type AffiliateDashboard as Dashboard,
} from '../../lib/affiliates';
import { ApiError } from '../../lib/apiClient';
import { useCopy } from '../../copy/CopyProvider';
import { formatMoney } from '../../lib/format';
import '../../styles/portals.css';

/**
 * The affiliate's contract-gated dashboard. Spec section 4b.
 *
 * NO AUTHENTICATION, BY DESIGN. An affiliate has no Voleto account; the contract number is
 * the entire credential. Everything that makes that safe lives in the engine (real entropy
 * in the number, a rate limiter on the endpoint, an identical answer for a wrong number and
 * an unknown one), so this screen deliberately does not try to be clever about it. It also
 * never says which part of a failed unlock was wrong.
 *
 * ONE DEVIATION FROM THE SPEC, FLAGGED RATHER THAN SILENTLY TAKEN.
 *
 * Section 4b item 4 asks for "Bar chart: Clicks vs. Tickets vs. Commission". Clicks and
 * tickets are counts; commission is dollars. Three bars on one axis where the third is a
 * different unit makes that bar's height meaningless, and putting it on a second scale is
 * a dual-axis chart, which is the single most common way a chart misleads.
 *
 * So the chart plots the two figures that ARE comparable, clicks and tickets, which is the
 * conversion funnel and the thing an affiliate actually wants to see. Commission keeps its
 * stat tile in the row above, where section 4b already puts it, at full precision. Nothing
 * is hidden and no figure is lost.
 */

const money = (c: number) => formatMoney(c);

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="vp-panel">
      <p className="vp-stat__label">{label}</p>
      <p className="vp-stat__value">{value}</p>
    </div>
  );
}

/**
 * Clicks against tickets, one count axis.
 *
 * ONE MEASURE ACROSS TWO CATEGORIES, not two series. Both bars therefore carry the same
 * fill, and the categories are told apart by their labels rather than by colour. That is
 * also what avoids a real problem: the spec's --accent-a and --accent-b are the two ends of
 * one gradient, and used instead as two series colours they sit at deltaE 14.9 in normal
 * vision, which is below the threshold where people can reliably tell two hues apart.
 *
 * Direct-labeled, so the figures are readable without a legend or a hover.
 */
function ConversionChart({ clicks, tickets }: { clicks: number; tickets: number }) {
  const max = Math.max(clicks, tickets, 1);
  const bars = [
    { label: 'Clicks', value: clicks },
    { label: 'Tickets sold', value: tickets },
  ];
  const rate = clicks > 0 ? Math.round((tickets / clicks) * 1000) / 10 : null;

  return (
    <div className="vp-panel">
      <h3 className="vp-gradient-heading vp-chart__title">Clicks and tickets</h3>
      <div className="vp-chart">
        {bars.map((b) => (
          <div key={b.label} className="vp-chart__row">
            <span className="vp-chart__label">{b.label}</span>
            <div className="vp-chart__track">
              <div
                className="vp-chart__bar"
                style={{ width: `${Math.max((b.value / max) * 100, b.value > 0 ? 2 : 0)}%` }}
              />
            </div>
            <span className="vp-chart__value mono">{b.value.toLocaleString('en-US')}</span>
          </div>
        ))}
      </div>
      {rate !== null && (
        <p className="vp-chart__note">
          {rate}% of clicks became a ticket.
        </p>
      )}
    </div>
  );
}

export function AffiliateDashboardPage() {
  const t = useCopy();
  const [contractNumber, setContractNumber] = useState('');
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function unlock(e: React.FormEvent) {
    e.preventDefault();
    if (!contractNumber.trim()) return;
    setBusy(true);
    setError(null);
    try {
      setData(await unlockAffiliateDashboard(contractNumber.trim()));
    } catch (err: unknown) {
      // One message for every failure. Saying "that contract is for another event" would
      // confirm which numbers exist, which is what an enumeration attack needs.
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Too many attempts. Wait a few minutes and try again.'
          : 'That contract number was not recognized.',
      );
      setData(null);
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!data?.affiliate.trackingLink) return;
    try {
      await navigator.clipboard.writeText(data.affiliate.trackingLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused in some contexts. The link is on screen and
      // selectable, so this is a convenience failing, not the feature failing.
      setCopied(false);
    }
  }

  return (
    <div className="vp-scope">
      {data?.background.videoUrl && (
        <video
          className="vp-bg-video"
          src={data.background.videoUrl}
          poster={data.background.posterUrl ?? undefined}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        />
      )}
      <div className="vp-bg-scrim" aria-hidden="true" />

      <main className="vp-shell">
        {!data ? (
          <form className="vp-contract-gate" onSubmit={unlock}>
            <h1 className="vp-gradient-heading">
              {t('affiliates.gate.heading', 'Affiliate dashboard')}
            </h1>
            <p className="vp-gate__lede">
              {t('affiliates.gate.lede', 'Enter the contract number you were given to see '
                + 'your clicks, ticket sales and commission.')}
            </p>
            <div className="vp-contract-gate__row">
              <label className="vp-visually-hidden" htmlFor="contract">Contract number</label>
              <input
                id="contract"
                className="vp-input"
                value={contractNumber}
                onChange={(ev) => setContractNumber(ev.target.value)}
                placeholder="AF-XXXX-XXXX"
                autoComplete="off"
                spellCheck={false}
              />
              <button type="submit" className="vp-btn-primary" disabled={busy}>
                {busy ? 'Checking…' : 'Load Contract'}
              </button>
            </div>
            <p
              className={`vp-contract-gate__msg${error ? ' vp-contract-gate__msg--error' : ''}`}
              role={error ? 'alert' : undefined}
            >
              {error ?? ''}
            </p>
          </form>
        ) : (
          <>
            {/* Identity card */}
            <section className="vp-panel vp-identity">
              {/* Decoration over real figures. aria-hidden because a screen reader
                  announcing it between every number would make the panel unreadable. */}
              <div className="vp-watermark" aria-hidden="true">
                {Array.from({ length: 12 }, (_, i) => (
                  <span key={i} className="vp-watermark__tile">
                    <span className="vp-watermark__id">{data.contractNumber}</span>
                    <span className="vp-watermark__label">CONFIDENTIAL</span>
                  </span>
                ))}
              </div>

              <h1 className="vp-gradient-heading">{data.affiliate.title}</h1>
              <p className="vp-identity__meta">
                Contract {data.contractNumber} | {data.event.title}
                {data.event.venueCity ? ` | ${data.event.venueCity}` : ''}
              </p>
              <p className="vp-identity__meta">
                {data.affiliate.commissionStartsAfter > 0
                  ? `Commission starts on ticket ${data.affiliate.commissionStartsAfter + 1}, `
                    + `counted ${data.affiliate.thresholdScope === 'lifetime'
                      ? 'across all your events' : 'per event'}.`
                  : 'Every attributed ticket earns commission.'}
              </p>
              <p className="vp-identity__meta">
                {data.stats.commissionableTickets.toLocaleString('en-US')} eligible
                {' '}of {data.stats.tickets.toLocaleString('en-US')} attributed tickets.
              </p>
            </section>

            {/* Tracking link */}
            {data.affiliate.trackingLink && (
              <section className="vp-panel">
                <h2 className="vp-gradient-heading vp-chart__title">
                  {t('affiliates.trackingLink.heading', 'Your tracking link')}
                </h2>
                <div className="vp-linkrow">
                  <code className="vp-linkrow__url">{data.affiliate.trackingLink}</code>
                  <button type="button" className="vp-btn-secondary" onClick={copyLink}>
                    {copied ? 'Copied' : 'Copy Link'}
                  </button>
                </div>
                {/* Named so the redirect does not look like a mistake. An affiliate seeing
                    a link that is not the show page will otherwise "correct" it to the
                    destination, which is the one that records nothing. */}
                <p className="vp-hint">
                  Share this exact link. It records the click, then sends people to{' '}
                  {data.affiliate.destinationUrl ?? 'the event page'}.
                </p>
              </section>
            )}

            {/* Four stat tiles */}
            <section className="vp-stat-row">
              <StatTile label="Clicks" value={data.stats.clicks.toLocaleString('en-US')} />
              <StatTile label="Tickets sold" value={data.stats.tickets.toLocaleString('en-US')} />
              <StatTile
                label="Commission per ticket"
                value={money(data.affiliate.commissionPerTicketCents)}
              />
              <StatTile label="Total commission" value={money(data.stats.commissionCents)} />
            </section>

            <ConversionChart clicks={data.stats.clicks} tickets={data.stats.tickets} />

            <div className="vp-card__actions">
              <button
                type="button"
                className="vp-btn-secondary"
                onClick={() => { setData(null); setContractNumber(''); }}
              >
                Lock dashboard
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
